import type {
    Iso8601DateTime,
    Uuid,
} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export enum UserAdditionalColumnType {
    image = "8c9c33b6-85c4-4a46-aab3-654404443c1e",
    json = "fc6bbe57-3b75-4b04-b06f-5f7ba2ac2d10",
    number = "1aa88529-bffa-4db5-9a23-fd3dfe919764",
    ou = "d9088984-2b17-4093-92b4-513373334136",
    ous = "4b5c1050-03e2-4f43-b3f2-6865e148ee08",
    // Retired after the select-options-to-UDO migration: select = "8857d40e-9a3e-415c-befc-626ce3f3e8a3",
    // TODO: Either this or text needs to be deprecated
    string = "c9502e96-57c4-4231-aa97-2ce6c68c7861",
    // TODO: Either this or string needs to be deprecated
    text = "2df71b5d-35e5-43c2-871e-e59cb8a2a2e3",
    timestamp = "6cc57077-232b-4a65-827b-70fd80eca9c7",
    user = "c563de89-f344-4cc8-afa5-965562edbece",
    userDefinedObject = "a204fd5a-3078-4f7e-a845-1dfb09363bb7",
    userDefinedObjects = "a06f661b-51b1-42ad-b558-301adc3bbd57",
    userTemplate = "6c00feb8-9a48-4d01-8821-25a6e4cb6359",
    uuid = "823eacbd-0a25-4311-b7d1-fc7fa2b515d0",
}

export type UserAdditionalColumnConfigurationMap = {
    [UserAdditionalColumnType.image]: { required: boolean };
    [UserAdditionalColumnType.json]: { ouTemplateId?: Uuid; required: boolean };
    [UserAdditionalColumnType.number]: { required: boolean };
    [UserAdditionalColumnType.ou]: { required: boolean };
    [UserAdditionalColumnType.ous]: { ouTemplateId: Uuid; required: boolean };
    [UserAdditionalColumnType.string]: { required: boolean };
    [UserAdditionalColumnType.text]: { required: boolean };
    [UserAdditionalColumnType.user]: { required: boolean };
    [UserAdditionalColumnType.userDefinedObject]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [UserAdditionalColumnType.userDefinedObjects]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [UserAdditionalColumnType.userTemplate]: { required: boolean };
    [UserAdditionalColumnType.uuid]: { required: boolean };
    [UserAdditionalColumnType.timestamp]: { required: boolean };
};

export type UserAdditionalColumnValueTypeMap = {
    [UserAdditionalColumnType.image]: string;
    [UserAdditionalColumnType.json]: string;
    [UserAdditionalColumnType.number]: number;
    [UserAdditionalColumnType.ou]: Uuid | null;
    [UserAdditionalColumnType.ous]: Array<Uuid>;
    [UserAdditionalColumnType.string]: string;
    [UserAdditionalColumnType.text]: string;
    [UserAdditionalColumnType.timestamp]: Iso8601DateTime;
    [UserAdditionalColumnType.user]: Uuid | null;
    [UserAdditionalColumnType.userDefinedObject]: Uuid | null;
    [UserAdditionalColumnType.userDefinedObjects]: Array<Uuid>;
    [UserAdditionalColumnType.userTemplate]: Uuid;
    [UserAdditionalColumnType.uuid]: Uuid;
};

export type UserTemplateAdditionalColumn = {
    displayName: string;
    id: Uuid;
} & {
    [K in UserAdditionalColumnType]: {
        configuration: UserAdditionalColumnConfigurationMap[K];
        type: K;
    };
}[UserAdditionalColumnType];

const userAdditionalColumnBaseValidator = zod.object({
    displayName: zod.string(),
    id: uuidZodValidator,
    resolver: zod.record(zod.string(), zod.unknown()).optional(),
});

export const userTemplateAdditionalColumnsZodValidator: zod.ZodType<UserTemplateAdditionalColumn> = zod.discriminatedUnion("type", [
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.image),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            ouTemplateId: uuidZodValidator.optional(),
            required: zod.boolean(),
        }),
        type: zod.literal(UserAdditionalColumnType.json),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.number),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.ou),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            ouTemplateId: uuidZodValidator,
            required: zod.boolean(),
        }),
        type: zod.literal(UserAdditionalColumnType.ous),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.string),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.text),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.user),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(UserAdditionalColumnType.userDefinedObject),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(UserAdditionalColumnType.userDefinedObjects),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.userTemplate),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.uuid),
    }),
    userAdditionalColumnBaseValidator.extend({
        configuration: zod.object({ required: zod.boolean() }),
        type: zod.literal(UserAdditionalColumnType.timestamp),
    }),
]);

// function getSchemaPropertyTypeFromUserAdditionalColumnType(additionalColumnType: UserAdditionalColumnType): SchemaPropertyType {
//     switch (additionalColumnType) {
//         // TODO: Revisit this, shouldn't it be uuid ??
//         case UserAdditionalColumnType.image: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.json: {
//             return SchemaPropertyType.json;
//         }
//         case UserAdditionalColumnType.number: {
//             return SchemaPropertyType.number;
//         }
//         case UserAdditionalColumnType.ou: {
//             return SchemaPropertyType.uuid;
//         }
//         // TODO: Verify this, shouldn't it be uuid[] ??
//         case UserAdditionalColumnType.ous: {
//             return SchemaPropertyType.json;
//         }
//         case UserAdditionalColumnType.select: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.string: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.text: {
//             return SchemaPropertyType.string;
//         }
//         case UserAdditionalColumnType.user: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.userTemplate: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.uuid: {
//             return SchemaPropertyType.uuid;
//         }
//         case UserAdditionalColumnType.timestamp: {
//             return SchemaPropertyType.timestamp;
//         }
//         default: {
//             throw new Error(`Unsupported additional column type: ${String(additionalColumnType)}`);
//         }
//     }
// }

// export function getSchemaPropertiesFromUserTemplateAdditionalColumns(
//     additionalColumns: Array<UserTemplateAdditionalColumn>,
//     //
// ): Record<string, SchemaProperty>
//  {
//     const properties: Record<string, SchemaProperty> = {};
//     for (const additionalColumn of additionalColumns) {
//         const columnNameInSnakeCase = toSnakeCase(additionalColumn.displayName);
//         if (properties[columnNameInSnakeCase] !== undefined) {
//             throw new Error(`Duplicate additional column display name: ${additionalColumn.displayName}`);
//         }
//         properties[columnNameInSnakeCase] = {
//             displayName: additionalColumn.displayName,
//             id: additionalColumn.id,
//             internalName: toCamelCase(additionalColumn.displayName),
//             required: additionalColumn.configuration.required,
//             type: getSchemaPropertyTypeFromUserAdditionalColumnType(additionalColumn.type),
//         };
//     }
//     return properties;
// }
